"use client";

import type { MoyasarInitResponse } from "@/lib/utils/moyasar";

export type ModalView =
  | "phone"
  | "password"
  | "register"
  | "set-password"
  | "enter-otp"
  | "reset-password"
  | "payment";

export type PaymentStatus =
  | "idle"
  | "processing"
  | "initiating"
  | "ready"
  | "success"
  | "error";

export interface DonationModalState {
  view: ModalView;
  phoneNumber: string;
  error: string | null;
  paymentStatus: PaymentStatus;
  paymentError: string | null;
  operationCode: string | null;
  donationId: number | null;
  moyasarConfig: MoyasarInitResponse | null;
}

export const initialDonationModalState: DonationModalState = {
  view: "phone",
  phoneNumber: "",
  error: null,
  paymentStatus: "idle",
  paymentError: null,
  operationCode: null,
  donationId: null,
  moyasarConfig: null,
};

export type DonationModalAction =
  | { type: "SET_VIEW"; view: ModalView }
  | { type: "SET_PHONE"; phoneNumber: string }
  | { type: "SET_ERROR"; error: string | null }
  | { type: "SET_PAYMENT_PROCESSING" }
  | { type: "SET_PAYMENT_INITIATING" }
  | {
      type: "SET_PAYMENT_READY";
      operationCode: string;
      donationId?: number | null;
      moyasarConfig: MoyasarInitResponse;
    }
  | { type: "SET_PAYMENT_ERROR"; message: string | null }
  | { type: "SET_PAYMENT_SUCCESS" }
  | { type: "SET_PAYMENT_IDLE" }
  | {
      type: "SET_OPERATION_CODE";
      operationCode: string | null;
      donationId?: number | null;
    }
  | { type: "SET_MOYASAR_CONFIG"; moyasarConfig: MoyasarInitResponse | null }
  | { type: "RESET" };

export function donationModalReducer(
  state: DonationModalState,
  action: DonationModalAction,
): DonationModalState {
  switch (action.type) {
    case "SET_VIEW":
      return { ...state, view: action.view };
    case "SET_PHONE":
      return { ...state, phoneNumber: action.phoneNumber };
    case "SET_ERROR":
      return { ...state, error: action.error };
    case "SET_PAYMENT_PROCESSING":
      return { ...state, paymentStatus: "processing", paymentError: null };
    case "SET_PAYMENT_INITIATING":
      return { ...state, paymentStatus: "initiating" };
    case "SET_PAYMENT_READY":
      return {
        ...state,
        paymentStatus: "ready",
        paymentError: null,
        operationCode: action.operationCode,
        donationId: action.donationId ?? null,
        moyasarConfig: action.moyasarConfig,
      };
    case "SET_PAYMENT_ERROR":
      return {
        ...state,
        ...(action.message != null && { paymentStatus: "error" as const }),
        paymentError: action.message,
      };
    case "SET_PAYMENT_SUCCESS":
      return { ...state, paymentStatus: "success" };
    case "SET_PAYMENT_IDLE":
      return {
        ...state,
        paymentStatus: "idle",
        paymentError: null,
        operationCode: null,
        donationId: null,
        moyasarConfig: null,
      };
    case "SET_OPERATION_CODE":
      return {
        ...state,
        operationCode: action.operationCode,
        donationId: action.donationId ?? state.donationId,
      };
    case "SET_MOYASAR_CONFIG":
      return { ...state, moyasarConfig: action.moyasarConfig };
    case "RESET":
      return initialDonationModalState;
    default:
      return state;
  }
}
